import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Pencil, Trash2, Briefcase, ChevronRight, Clock } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError, isLeader, fmtDuration } from "@/lib/api";
import { currentLocale } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { JobDialog, NewButton } from "@/components/RecordDialogs";
import { QuickJobDialog } from "@/components/QuickJobDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const badge = {
  planned: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  done: "bg-green-50 text-green-700 border-green-200",
  ready_to_invoice: "bg-teal-50 text-teal-700 border-teal-200",
  invoiced: "bg-indigo-50 text-indigo-700 border-indigo-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

const CLOSED = ["done", "ready_to_invoice"];
const isInvoiced = (j) => j.status === "invoiced" || j.invoiced;

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString(currentLocale(), { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "–";

export default function Jobs() {
  const { t } = useTranslation();
  const { user, company } = useAuth();
  const navigate = useNavigate();
  const leader = isLeader(user?.role) && !company?.demo_readonly;
  const [jobs, setJobs] = useState([]);
  const [quickOpen, setQuickOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [status, setStatus] = useState("all");
  const [tab, setTab] = useState("active");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/jobs", { params: status !== "all" ? { status } : {} });
      setJobs(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/users").then(({ data }) => setStaff(data)).catch(() => {});
  }, []);

  const remove = async (job) => {
    if (!window.confirm(t("jobs.deleteConfirm", { title: job.title }))) return;
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success(t("jobs.deleted"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleTime = null;
  const inTab = (j) => {
    if (isInvoiced(j)) return tab === "invoiced";
    if (tab === "invoiced") return false;
    return tab === "closed" ? CLOSED.includes(j.status) : !CLOSED.includes(j.status);
  };
  const counts = {
    active: jobs.filter((j) => !isInvoiced(j) && !CLOSED.includes(j.status)).length,
    closed: jobs.filter((j) => !isInvoiced(j) && CLOSED.includes(j.status)).length,
    invoiced: jobs.filter(isInvoiced).length,
  };
  const filtered = jobs.filter(
    (j) =>
      inTab(j) &&
      `${j.order_number || ""} ${j.title} ${j.customer_name || ""} ${j.address || ""}`
        .toLowerCase()
        .includes(q.toLowerCase())
  );

  const staffName = (ids) =>
    (ids || []).map((id) => staff.find((s) => s.id === id)?.name).filter(Boolean).join(", ") || t("jobs.notAssigned");

  return (
    <div data-testid="jobs-page">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{t("jobs.title")}</h1>
          <p className="text-sm text-slate-500">{filtered.length} {t("common.records")}</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {!leader && company?.features?.quick_order && (
            <NewButton
              testid="jobs-manual-new-button"
              label="Ny arbetsorder"
              onClick={() => setQuickOpen(true)}
            />
          )}
          {leader && (
            <NewButton testid="jobs-new-button" label={t("jobs.newJob")} onClick={() => { setActive(null); setOpen(true); }} />
          )}
        </div>
      </div>

      <div className="mb-3 flex overflow-hidden rounded-md border border-slate-200 bg-white" data-testid="jobs-tabs">
        {["active", "closed", "invoiced"].map((k) => (
          <button
            key={k}
            data-testid={`jobs-tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium transition-colors duration-200 ${
              tab === k ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            {t(`jobs.tab_${k}`)} ({counts[k]})
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <Input
          data-testid="jobs-search-input"
          placeholder={t("jobs.searchPlaceholder")}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-10 max-w-xs bg-white"
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="jobs-status-filter" className="h-10 w-40 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">{t("jobs.allStatuses")}</SelectItem>
            {["planned", "in_progress", "done", "ready_to_invoice", "invoiced", "cancelled"].map((k) => (
              <SelectItem key={k} value={k} data-testid={`jobs-filter-${k}`}>
                {t(`jobStatus.${k}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="jobs-empty">
            {t("jobs.empty")}
          </div>
        )}
        {filtered.map((j) => {
          return (
          <div
            key={j.id}
            data-testid={`job-row-${j.id}`}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/arbetsorder/${j.id}`)}
            onKeyDown={(e) => { if (e.key === "Enter") navigate(`/arbetsorder/${j.id}`); }}
            className="cursor-pointer rounded-lg border border-slate-200 bg-white p-3 transition-colors duration-200 hover:border-blue-300 hover:bg-blue-50/40"
          >
            <div className="flex flex-wrap items-start gap-3" onClick={(e) => e.stopPropagation()}>
              <div
                className="min-w-0 flex-1 cursor-pointer"
                onClick={() => navigate(`/arbetsorder/${j.id}`)}
              >
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 shrink-0 text-slate-400" />
                  <span className="rounded bg-slate-900 px-1.5 py-0.5 text-[11px] font-bold text-white" data-testid={`job-number-${j.id}`}>
                    #{j.order_number ? String(j.order_number).padStart(4, "0") : "–"}
                  </span>
                  <h3 className="truncate text-base font-semibold text-slate-900">{j.title}</h3>
                  <span className={`rounded border px-1.5 py-0.5 text-[11px] font-medium ${badge[j.status]}`} data-testid={`job-status-${j.id}`}>
                    {t(`wf.${j.workflow_status || "not_started"}`)}
                  </span>
                  {j.job_type === "installation" && (
                    <span
                      className="rounded border border-purple-200 bg-purple-50 px-1.5 py-0.5 text-[11px] font-semibold lowercase text-purple-800"
                      data-testid={`job-installation-badge-${j.id}`}
                    >
                      Nymontering
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                  <span data-testid={`job-time-${j.id}`}>
                    {fmt(j.start)}
                    {j.end
                      ? j.end.slice(0, 10) === j.start?.slice(0, 10)
                        ? `–${j.end.slice(11, 16)}`
                        : ` – ${fmt(j.end)}`
                      : ""}
                  </span>
                  {j.customer_name && <span className="text-slate-500">{j.customer_name}</span>}
                  <span className="text-slate-500">{staffName(j.assigned_to)}</span>
                </div>
                {j.address && (
                  <a
                    data-testid={`job-maps-link-${j.id}`}
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(j.address)}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 transition-colors duration-200 hover:text-blue-700"
                  >
                    <MapPin className="h-4 w-4" /> {j.address}
                  </a>
                )}
                <div className="mt-1.5 flex items-center gap-2 text-sm" data-testid={`job-time-total-${j.id}`}>
                  <Clock className="h-3.5 w-3.5 text-slate-400" />
                  <span className="font-medium text-slate-700">{fmtDuration(j.work_seconds || 0)}</span>
                  {(j.travel_out_seconds || j.travel_home_seconds) ? (
                    <span className="text-slate-500">
                      {t("common.travel")} {fmtDuration((j.travel_out_seconds || 0) + (j.travel_home_seconds || 0))}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  size="sm"
                  data-testid={`job-open-${j.id}`}
                  onClick={() => navigate(`/arbetsorder/${j.id}`)}
                  className="h-9 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
                >
                  {t("common.open")} <ChevronRight className="ml-1 h-3.5 w-3.5" />
                </Button>
                {leader && (
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`job-edit-${j.id}`}
                    onClick={() => { setActive(j); setOpen(true); }}
                    className="h-9 bg-white transition-colors duration-200 hover:bg-slate-100"
                  >
                    <Pencil className="mr-1.5 h-3.5 w-3.5" /> {t("jobs.editBtn")}
                  </Button>
                )}
                {leader && (
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid={`job-delete-${j.id}`}
                    onClick={() => remove(j)}
                    className="h-9 border-red-200 bg-white text-red-600 transition-colors duration-200 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        );})}
      </div>

      <JobDialog open={open} onOpenChange={setOpen} job={active} staff={staff} onSaved={load} onManual={company?.features?.quick_order ? () => setQuickOpen(true) : undefined} />
      <QuickJobDialog open={quickOpen} onOpenChange={setQuickOpen} staff={staff} onSaved={load} />
    </div>
  );
}